import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@/icons';
import { authApiService } from '../services/authApiService';
import { debugError } from '../utils/debug';

export const ResendTeamInvitePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;

    const token = (searchParams.get('token') || '').trim();
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        document.title = 'Resend team invitation | Calculist';
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;
    if (!token) return <Navigate to="/" replace />;

    const handleResend = async () => {
        setError(null);
        setSubmitting(true);
        try {
            await authApiService.signupResendTeamInvite(token);
            setDone(true);
        } catch (err: unknown) {
            debugError('signupResendTeamInvite', err);
            setError(err instanceof Error ? err.message : 'Could not resend invitation. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '480px', width: '100%' }}>
                <div style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.lg }} />

                    {done ? (
                        <>
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: '72px', height: '72px', borderRadius: '50%',
                                backgroundColor: '#f0fdf4', color: '#16a34a', fontSize: '32px',
                                margin: '0 auto',
                                marginBottom: theme.spacing.md,
                            }}>
                                <FontAwesomeIcon icon={faCheckCircle} />
                            </div>
                            <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Invitation sent</h1>
                            <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>A fresh invitation link has been sent to the team member.</p>
                        </>
                    ) : (
                        <>
                            <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>Resend team invitation</h1>
                            <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>A team member's invitation link has expired. Click the button below to send them a fresh link.</p>
                        </>
                    )}
                </div>

                {!done && (
                    <>
                        {error && (
                            <div style={{ marginBottom: theme.spacing.md, padding: theme.spacing.md, backgroundColor: '#fef2f2', border: `1px solid ${theme.colors.status.error}`, borderRadius: '6px', fontSize: theme.typography.fontSize.sm, color: theme.colors.status.error }}>
                                {error}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => void handleResend()}
                            disabled={submitting}
                            style={{
                                ...themedStyles.button.primary,
                                ...(submitting ? themedStyles.button.disabled : {}),
                                width: '100%',
                                padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                            }}
                        >
                            {submitting ? 'Sending…' : 'Resend Invitation'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
